import React, { useEffect, useState, useRef } from 'react';
import { Box, Slide } from '@mui/material';
import ToastMessage from '../../../../packages/ui/templates/toast-message';
import { TOAST_TYPES } from '../../../../constants/CommonConstants';
import { useDispatch, useSelector } from 'react-redux';
import { commonActionCreators } from '../../../../redux/modules/common';
import { getProfile } from '../../../../redux/modules/profile/selectors';
import { useStyles } from './VideoCallScreen.styles';
import AppointmentInfo from '../appointment-info';
import { getNextAppointment } from '../../../../services/appointment/appointment.service';
import { MemberDrawerController } from '../../../../components/member-drawer-controller';
import { getFetchNextAppointment } from '../../../../redux/modules/common/selectors';
import { StreamContainer } from './components/StreamContainer';
import { SessionContent } from './components/SessionContent';
import WaitingRoomTopBar from '../end-session-topbar';
import { TWILIO_CALL_STATE } from '../../../../packages/ui/templates/twilio-call/twilio.constants';
import { LoadingState, PanelBody, RightPanel } from '../sign-off-v4/components/RightPanel';
import Giggle from '../giggle';
import { btnType, Button, colors, Icons, Text, textLevel } from '../../../../packages';
import { MEMBER_ACTIONS } from '../../../../packages/ui/templates/sessions/sessions.constants';
import AskMeAnything from '../../../../components/ask-me-anything';
import { WaitingRoomDetails } from '../waiting-room-details/WaitingRoomDetails';

/**
 * VideoCallScreen Component
 * 3-column layout:
 * - Left: Waiting room details (visible when isWaiting)
 * - Middle: Video stream container (always visible)
 * - Right: Session content (visible when !isWaiting)
 */
const VideoCallScreen = ({
  isWaiting,
  setIsWaiting,
  appointment,
  isEndedSession,
  onEndSession,
  endSession,
  telesessionToken,
  telesessionId,
  careTeamList,
  isInPersonSessionCall,
  startInPersonSession,
  onRestartSessionClick,
  onMemberEndedSession, // Callback when member ends session
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { demographicDetails } = useSelector(getProfile);
  const [openGiggle, setOpenGiggle] = useState(false);
  const nextAppointmentFetching = useSelector(getFetchNextAppointment);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [showCompletedByPhone, setShowCompletedByPhone] = useState(false);
  const [isCompletedByPhone, setIsCompletedByPhone] = useState(false);
  const [nextGeneralAppointment, setNextGeneralAppointment] = useState(null);
  const [duration] = useState(0);
  const [toastProperties, setToastProperties] = useState({
    message: '',
    type: TOAST_TYPES.SUCCESS,
    status: false,
  });
  const [isGracePeriodExpired, setIsGracePeriodExpired] = useState(false);
  const [showAMAInline, setShowAMAInline] = useState(false);
  const giggleKeywords = ['giggle', 'laugh', 'chuckle'];
  const getRandomKeyword = () => giggleKeywords[Math.floor(Math.random() * giggleKeywords.length)];

  // Ref to hold the sendSessionEndedSignal function from StreamContainer
  const sessionEndSignalRef = useRef(null);

  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(40);
  const isDraggingRef = useRef(false);

  // Scratchpad state passed from SessionContent
  const [scratchpadData, setScratchpadData] = useState({ scratchpad: '', addScratchpadNotes: null });
  const [currentSection, setCurrentSection] = useState(null);

  const handleMemberActionClick = type => {
    if (isWaiting && type === MEMBER_ACTIONS.AMA) {
      setShowAMAInline(true);
    } else {
      dispatch(commonActionCreators.openDrawer(type));
    }
  };

  const handleCloseInlineAMA = () => {
    setShowAMAInline(false);
  };

  const handleMouseDown = () => {
    isDraggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handleMouseMove = e => {
      if (!isDraggingRef.current) return;
      const container = document.querySelector('[data-resizable-container]');
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      if (newWidth >= 30 && newWidth <= 70) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (!isWaiting) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isWaiting]);

  /**
   * Handle grace period expiration from waiting room
   */
  const handleGracePeriodExpired = () => {
    setIsGracePeriodExpired(true);
  };

  /**
   * Handle Twilio call state changes
   * When call connects, exit waiting room to show session view
   */
  const handleTwilioCallStateChange = callState => {
    if (callState === TWILIO_CALL_STATE.CONNECTED && isWaiting) {
      setIsWaiting(false);
    }
  };

  /**
   * Handle member joining session while Twilio call is active
   */
  const handleMemberJoinedWithActiveCall = () => {
    setToastProperties({
      message: 'Member has joined session. Disconnecting voice call.',
      type: TOAST_TYPES.INFO,
      status: true,
    });
  };

  /**
   * Handle session end from provider side
   * Sends session-ended signal to member via OpenTok, then calls parent endSession
   */
  const handleEndSession = () => {
    // Send the session-ended signal to the member via OpenTok
    if (sessionEndSignalRef.current) {
      sessionEndSignalRef.current();
    }

    // Call the parent endSession callback (which will call API and set isEndedSession)
    endSession();
  };

  const fetchNextAppointment = async () => {
    try {
      await getNextAppointment(appointment.participantId, true, appointment.endTime).then(({ data }) => {
        setNextAppointment(data?.appointment || undefined);
      });
      await getNextAppointment(null, true, appointment.endTime).then(({ data }) => {
        setNextGeneralAppointment(data?.appointment || undefined);
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (appointment) {
      void fetchNextAppointment();
      if (nextAppointmentFetching) {
        dispatch(commonActionCreators.fetchNextAppointment(false));
      }
    }
  }, [appointment, nextAppointmentFetching]);

  /**
   * Reset inline AMA when leaving waiting room
   */
  useEffect(() => {
    if (!isWaiting) {
      setShowAMAInline(false);
    }
  }, [isWaiting]);

  /**
   * Wrapper for onEndSession to track when session is completed by phone
   */
  const handleEndSessionWithTracking = type => {
    if (type === 'PHONE') {
      setIsCompletedByPhone(true);
    }
    onEndSession?.(type);
  };

  return (
    <>
      {/* 3-Column Layout */}
      <Box className={classes.rootContainer} data-resizable-container>
        {/* LEFT SECTION - Stream Container */}
        {!isEndedSession && !isInPersonSessionCall && (
          <Box
            className={classes.streamSection}
            sx={{
              flex: isWaiting ? 1 : 'none',
              width: isWaiting ? 'auto' : `${leftPanelWidth}%`,
              transition: isWaiting ? 'flex 0.3s ease-in-out' : 'none',
            }}
          >
            <StreamContainer
              appointment={appointment}
              telesessionId={telesessionId}
              telesessionToken={telesessionToken}
              isInPersonSessionCall={isInPersonSessionCall}
              isWaiting={isWaiting}
              setIsWaiting={setIsWaiting}
              setShowCompletedByPhone={setShowCompletedByPhone}
              duration={duration}
              handleMemberActionClick={handleMemberActionClick}
              onSessionEndSignalRef={sessionEndSignalRef}
              onMemberEndedSession={onMemberEndedSession}
              onGracePeriodExpired={handleGracePeriodExpired}
              onTwilioCallStateChange={handleTwilioCallStateChange}
              onMemberJoinedWithActiveCall={handleMemberJoinedWithActiveCall}
              scratchpadData={scratchpadData}
              currentSection={currentSection}
            />
          </Box>
        )}

        {/* RESIZER */}
        {!isWaiting && !isEndedSession && !isInPersonSessionCall && (
          <Box
            onMouseDown={handleMouseDown}
            sx={{
              cursor: 'col-resize',
              position: 'relative',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover .resizer-handle': {
                backgroundColor: colors.white35,
              },
            }}
          >
            <Box
              className="resizer-handle"
              sx={{
                width: '6px',
                height: '120px',
                backgroundColor: colors.white,
                borderRadius: '999px',
                transition: 'background-color 0.2s',
              }}
            />
          </Box>
        )}

        {/* RIGHT SECTION - Waiting Room Details */}
        <Slide direction="left" in={isWaiting} mountOnEnter unmountOnExit timeout={300}>
          <Box className={classes.waitingSectionWrapper}>
            {/* Main Content Container */}
            <Box className={classes.waitingSectionContainer}>
              {isWaiting && !isEndedSession && (
                <WaitingRoomTopBar
                  appointment={appointment}
                  onEndSession={handleEndSessionWithTracking}
                  startInPersonSession={startInPersonSession}
                  showCallCompletedByPhone={showCompletedByPhone}
                  showInPersonSession
                  showMarkAsNoShow={isGracePeriodExpired}
                  showAMAInline={showAMAInline}
                  onCloseAMA={handleCloseInlineAMA}
                />
              )}
              {showAMAInline ? (
                <Box className={classes.waitingSectionAMA}>
                  <AskMeAnything
                    useDrawer={false}
                    memberId={appointment?.patientId || appointment?.participantId}
                  />
                </Box>
              ) : (
                <Box className={classes.waitingSectionBody}>
                  <AppointmentInfo appointment={appointment} />
                  <WaitingRoomDetails appointment={appointment} />
                  {/* <Priorities
                    hideNotes
                    currentAppointment={appointment}
                    handleMemberActionClick={handleMemberActionClick}
                  /> */}
                </Box>
              )}
            </Box>

            {/* Footer Container - Separate from main content (hidden when AMA is showing) */}
            {!showAMAInline && (
              <Box
                className={classes.waitingSectionFooter}
                onClick={() => handleMemberActionClick(MEMBER_ACTIONS.AMA)}
              >
                <Box display="flex" alignItems="center" gap="12px">
                  <Text level={textLevel.L}>✨</Text>
                  <Text className={classes.footerText}>
                    Ask co-pilot to start preparing for your appointment
                  </Text>
                </Box>
                <Button
                  variant={btnType.OUTLINE}
                  className={classes.footerBtn}
                  onClick={() => handleMemberActionClick(MEMBER_ACTIONS.AMA)}
                >
                  <Icons glyph="arrow-right" size={20} />
                </Button>
              </Box>
            )}
          </Box>
        </Slide>

        {/* RIGHT SECTION - Session Content (shown conditionally when not in waiting room) */}
        {!isWaiting && (
          <Box
            sx={{
              flex: 'auto',
              width: `${100 - leftPanelWidth}%`,
              minWidth: isEndedSession ? '600px' : '0px',
            }}
          >
            <SessionContent
              appointment={appointment}
              endSession={handleEndSession}
              setToastProperties={setToastProperties}
              nextAppointment={nextAppointment}
              nextGeneralAppointment={nextGeneralAppointment}
              handleMemberActionClick={handleMemberActionClick}
              onRestartSessionClick={onRestartSessionClick}
              sessionEnded={isEndedSession}
              setSessionEnded={() => {}} // No-op since parent controls this now
              demographicDetails={demographicDetails}
              onScratchpadUpdate={data => {
                setScratchpadData(data);
                setCurrentSection(data.currentSection);
              }}
              showMarkAsNoShow={isGracePeriodExpired}
            />
          </Box>
        )}
        {isEndedSession && !isCompletedByPhone && (
          <Box
            sx={{
              flex: '0 1 auto',
              minWidth: { md: '200px' },
              maxWidth: { xs: '100%', sm: '100%', md: '300px', lg: '400px', xl: '500px' },
              display: { xs: 'none', sm: 'none', md: 'flex' },
              flexDirection: 'column',
            }}
          >
            <RightPanel>
              <PanelBody>
                <LoadingState
                  message="Give us 5 mins"
                  description="In 5 minutes, we'll complete your session note & treatment plan updates. You can sign off on the note in your To Dos."
                  onGiggle={() => setOpenGiggle(true)}
                />
              </PanelBody>
            </RightPanel>
          </Box>
        )}
      </Box>

      {/* Member Drawer Controller */}
      <MemberDrawerController
        appointment={appointment}
        careTeamList={careTeamList}
        renderButtons={false}
        isInPersonSessionCall={isInPersonSessionCall}
        chatIconGlyph="message"
      />
      <Giggle
        open={openGiggle}
        setOpen={setOpenGiggle}
        keyword={getRandomKeyword()}
        heading={"Let's giggle!"}
      />

      {/* Toast Notifications */}
      <ToastMessage
        show={toastProperties.status}
        snackType={toastProperties.type}
        snackMessage={toastProperties.message}
        clearSnackbarAction={() => {
          setToastProperties({ message: '', type: toastProperties.type, status: false });
        }}
      />
    </>
  );
};

export { VideoCallScreen };
